<script setup>
    import { useUserStore } from '@/stores/user';
    import { useRouteQuery } from '@vueuse/router'
    
    const emit = defineEmits(['updateSites', 'resetSites', 'close']);    

    const route = useRoute();
    const userStore = useUserStore();

    const nearMe = useRouteQuery('nearMe', false);
    const sortBy = useRouteQuery('sortBy', 'alpha');
    const siteTypeFilters = useRouteQuery('siteTypeFilters', []);
    const amenitiesFilters = useRouteQuery('amenitiesFilters', []);
    const datesFilters = useRouteQuery('datesFilters', '');
    

    const props = defineProps({
        sites: {
            type: Array,
            required: true,
            default: [],
        },
        mapView: {
            type: Boolean,
            required: false,
            default: true,
        },
        ohcFavoriteSites: {
            type: Array,
            required: false,
            default: [],
        },
    });
    
    const { sites, ohcFavoriteSites } = toRefs(props);

    // "My Favorites" only means anything to someone who has some. The prop was
    // already being passed and destructured but never read, so the option was
    // offered to everyone and did nothing for most of them.
    const hasFavorites = computed(() => Boolean(ohcFavoriteSites?.value?.length));

    const isFramed = ref(route.path.includes('iframe'));

    const applyActive = ref(true);

    const filterOptions = ref({
        datesFilters: '',
        siteTypeFilters: [],
        amenitiesFilters: [],
        nearMe: false,
        sortBy: 'alpha',
    });

    // The panel is held open with v-show, not v-if, so it mounts once and
    // querytoFilters() never runs again. Someone can therefore unfavourite
    // their last site from the list behind it while "My Favorites" is still
    // selected. Without this the option vanishes and the select renders blank.
    watch(hasFavorites, (has) => {
        if (!has && filterOptions.value.sortBy === 'favorites') {
            filterOptions.value.sortBy = 'alpha';
        }
    });

    const amenities = [
        { label: 'Family Friendly', value: 'Family Accessible', svg: 'SvgsAmenitiesFamily' },
        { label: 'Restrooms Available', value: 'Washrooms Available', svg: 'SvgsAmenitiesRestroom' },
        { label: 'Wheelchair Accessible', value: 'Wheelchair Accessible', svg: 'SvgsAmenitiesWheelchair' },
        { label: 'Photography Allowed', value: 'Photography Permitted', svg: 'SvgsAmenitiesPhotography' },
        {
            label: 'Refreshments Available',
            value: 'Refreshments Available Complimentary',
            svg: 'SvgsAmenitiesRefreshmentsFree',
        },
        {
            label: 'Refreshments for Purchase',
            value: 'Refreshments Available for Sale',
            svg: 'SvgsAmenitiesRefreshmentsBuy',
        },
    ];

    // Derived from FESTIVAL_DAYS rather than written out here. This list used to
    // be a hardcoded table and it still read the 2025 weekend after rollover —
    // Asana O2, "Filters for site days are the wrong dates". The `value`s are
    // Airtable field names and were always right, so only the labels lied, which
    // is exactly why it survived four days of a live festival site.
    const dates = FESTIVAL_DAYS.map((day) => ({ label: festivalDayLabel(day.date), value: day.field }));

    const siteTypes = computed(() => {
        let types = [];
        if (sites?.value?.length) {
            for (let i = 0; i < sites.value.length; i++) {
                const siteType = sites.value[i].fields['Site Type'];
                if (siteType && (siteType !== '') & !types.includes(siteType)) {
                    types.push(siteType);
                }
            }
        }
        return types;
    });

    function applyFilters(persist = true) {
        // Read the DRAFT (filterOptions), not the applied route query. `sortBy`
        // here is a useRouteQuery ref, so at this point it still holds the
        // PREVIOUS sort — the new one is only written by filtersToQuery() below.
        // Testing it meant choosing Distance and pressing Apply never asked for
        // a location: the request only went out on the *next* Apply, by which
        // time the query had caught up. That is the "it only works after
        // applying a different filter and toggling back" report (Asana O2-54).
        // Nothing else is needed to make the list re-sort — the site list is a
        // computed over locationStore.coords, so it reorders on its own once the
        // fix arrives. `nearMe` was always read from the draft, which is why
        // Near Me worked first time and Distance did not.
        if (filterOptions.value.sortBy === 'distance' || filterOptions.value.nearMe) {
            userStore.setHasLocationPermission(true);
        }
        filtersToQuery();
    }

    // Near Me is a proximity filter, so proximity is the order people expect
    // inside it. It only narrowed to a mile and left the list alphabetical, which
    // put a site 0.8 miles away above one 0.3 miles away (Asana O2-41, reported
    // against ?nearMe=true with no sort of its own).
    //
    // Moving the sort dropdown rather than quietly overriding the sort keeps the
    // control honest about what the list is doing, and leaves "A to Z" selectable
    // if someone does want alphabetical within the mile.
    watch(
        () => filterOptions.value.nearMe,
        (on, wasOn) => {
            if (on && !wasOn && filterOptions.value.sortBy === 'alpha') {
                filterOptions.value.sortBy = 'distance';
            } else if (!on && wasOn && filterOptions.value.sortBy === 'distance') {
                filterOptions.value.sortBy = 'alpha';
            }
        },
    );

    function resetAll() {
        // Reset to the DECLARED defaults. The previous version set sortBy and
        // nearMe correctly and then looped over every key assigning [], which
        // undid both of them one line later (Asana O2-50).
        //
        // An array-valued nearMe is what broke the Near Me filter. The checkbox
        // is v-model'd to it with value="true", and Vue treats an array-bound
        // checkbox as a GROUP — so ticking it pushed the string "true" into the
        // array rather than setting a boolean. Two things then went wrong:
        //
        //   - the watcher below guards on `on && !wasOn`, and an EMPTY ARRAY is
        //     truthy in JS, so !wasOn was false and the sort never flipped to
        //     Distance — the list stayed alphabetical and looked unfiltered;
        //   - Sites/page.vue tests `nearMe === true || nearMe === 'true'`, which
        //     an array does not satisfy.
        //
        // Only reachable after pressing Reset, which is exactly how it was
        // reported: the filter works on a fresh page and stops working once
        // Reset has been used.
        filterOptions.value = {
            datesFilters: '',
            siteTypeFilters: [],
            amenitiesFilters: [],
            nearMe: false,
            sortBy: 'alpha',
        };
        filtersToQuery();
    }

    function filtersToQuery() {
        trackFilterChanges();
        nearMe.value = filterOptions.value.nearMe;
        siteTypeFilters.value = coerceArray(filterOptions.value.siteTypeFilters);
        amenitiesFilters.value = coerceArray(filterOptions.value.amenitiesFilters);
        datesFilters.value = filterOptions.value.datesFilters;
        sortBy.value = filterOptions.value.sortBy;
        emit('close');
    }

    // Emits one filter_applied event per non-default filter category when the user hits Apply.
    // "Applied" (rather than per-keystroke) matches the event's naming and avoids noisy events
    // while a user is still exploring checkboxes before confirming their selection.
    function trackFilterChanges() {
        const { track } = useAnalytics();
        const defaults = { nearMe: false, siteTypeFilters: [], amenitiesFilters: [], datesFilters: '', sortBy: 'alpha' };
        for (const filter_type of Object.keys(defaults)) {
            const value = filterOptions.value[filter_type];
            const defaultValue = defaults[filter_type];
            const isActive = Array.isArray(value) ? value.length > 0 : value !== defaultValue;
            if (isActive) {
                track('filter_applied', { filter_type, value: JSON.stringify(value) });
            }
        }
    }

    function coerceArray(val) {
        if (typeof val === 'string') return [val];
        if (Array.isArray(val)) return val;
        return [];
    }

    function querytoFilters() {
        filterOptions.value.nearMe = nearMe.value;
        filterOptions.value.siteTypeFilters = coerceArray(siteTypeFilters.value);
        filterOptions.value.amenitiesFilters = coerceArray(amenitiesFilters.value);
        filterOptions.value.datesFilters = datesFilters.value;
        // A ?sortBy=favorites can outlive the favourites themselves — a shared
        // link, or unfavouriting the last site and reopening the panel. The
        // option is not rendered in that state, so seeding the select with it
        // would leave the control showing nothing at all.
        filterOptions.value.sortBy = sortBy.value === 'favorites' && !hasFavorites.value
            ? 'alpha'
            : sortBy.value;
    }

    onMounted(() => {
        querytoFilters();
    });

    // The panel stays mounted, so a filter change made outside it (the list's
    // "Clear filters" button, back/forward) has to reach the draft too, or the
    // panel reopens showing filters that are no longer applied.
    watch([nearMe, siteTypeFilters, amenitiesFilters, datesFilters, sortBy], querytoFilters);
    
</script>

<template>
    <div class="sites-filters" :class="{ framed: isFramed }">
        <div class="sites-filters-inner">
            <div class="header">
                <button type="button" class="back-caret" @click.prevent="emit('close')" aria-label="Close">
                    <SvgsBackCaret />
                </button>
                <h2 class="subhead-2">Filter Sites</h2>
                <button type="button" class="reset small-heavy uppercase" @click="resetAll">Reset</button>
            </div>
            <!-- Sort By -->
            <label class="card-header" for="sites-sort-select">Sort By:</label>
            <div class="select form-field sort-by">
                <select id="sites-sort-select" v-model="filterOptions.sortBy">
                    <option value="alpha">A to Z</option>
                    <option value="distance">Distance</option>
                    <option v-if="hasFavorites" value="favorites">My Favorites</option>
                </select>
                <SvgsSelectArrow class="select-arrow" />
            </div>
            <!-- Day. Named as on the events filter. Still radios with a "Both
                 days" option, because here it means open on BOTH days (see
                 utils/siteFiltering.ts), not "either day" as on events. -->
            <div class="checkboxes">
                <fieldset>
                    <legend>Day</legend>
                    <div class="checkbox-wrapper" v-for="(date, index) in dates" :key="date.value + index">
                        <label>{{ date.label }}<input type="radio" v-model="filterOptions.datesFilters" :value="date.value" /></label>
                    </div>
                    <div class="checkbox-wrapper">
                        <label>Both days<input type="radio" v-model="filterOptions.datesFilters" value="all" /></label>
                    </div>
                </fieldset>
            </div>
            <!-- Site Types -->
            <div class="checkboxes">
                <fieldset>
                    <legend>Site Type</legend>
                    <div class="checkbox-wrapper">
                        <label>
                            Near Me (< 1 mile)
                            <input type="checkbox" v-model="filterOptions.nearMe" :disabled="userStore.gettingLocation" value="true" />
                        </label>
                    </div>                    
                    <div class="checkbox-wrapper" v-for="(type, index) in siteTypes" :key="type + index">
                        <label>
                            {{ siteTypeLabel(type) }}
                            <input type="checkbox" v-model="filterOptions.siteTypeFilters" :value="type" />
                        </label>
                    </div>
                </fieldset>
            </div>
            <!-- Amenities -->
            <div class="checkboxes amenities">
                <fieldset>
                    <legend>Accessibility & Amenities</legend>
                    <div class="checkbox-wrapper" v-for="(amen, index) in amenities" :key="amen.value + index">
                        <label>
                            <component :is="amen.svg" />
                            <span>{{ amen.label }}</span>
                            <input type="checkbox" v-model="filterOptions.amenitiesFilters" :value="amen.value" />
                        </label>
                    </div>
                </fieldset>
            </div>
            <!-- Button -->
            <div class="apply-button-wrapper">
                <div class="container hd-container">
                    <button type="button" class="apply-cancel-buttons apply-button" @click="applyFilters" :disabled="!applyActive" :class="{ active: applyActive }">Apply</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .sites-filters {
        height: 100%;
        overflow: hidden;
        overflow-y: scroll;
        padding: 16px;
        padding-bottom: calc(98px + env(safe-area-inset-bottom));

        @media (min-width: $tablet) {
            padding: 64px 64px 0px 64px;
        }

        .header {
            position: relative;
            text-align: center;
            margin-bottom: 34px;
            
            .reset {
                position: absolute;
                right: 0;
                top: 2px;
            }
            
            .back-caret {
                position: absolute;
                left: 0;
                top: -3px;
            }
        }

        .sort-by {
            margin-top: 16px;
        }

        .amenities {
            @media (min-width: $tablet) {
                margin-bottom: 60px;
            }
        }
    }

    .apply-button-wrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background: white;
        // Clear of the iPhone home indicator (viewport-fit=cover is on).
        padding: 10px 0 calc(10px + env(safe-area-inset-bottom));
        z-index: 9999;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
        @media (min-width: $tablet) {
            padding: 20px 0 calc(20px + env(safe-area-inset-bottom));
        }
    }

    .location-loading {
        font-size: 0.85em;
        color: #666;
        font-style: italic;
    }
    
    .location-error {
        font-size: 0.85em;
        color: #cc0000;
        cursor: help;
    }
</style>
